/**
 * BMO brand style configuration — colors, typography, and prompt rules
 * reverse-engineered from the BMO "Company Profile Examples" template
 * (slide master + layouts, extracted via the OOXML slide master).
 *
 * Mirrors the shape of `citiStyleConfig.ts`. When BMO style is active the
 * prompt below is appended to the system prompt and the color/theme
 * constants are used as renderer defaults.
 *
 * Fidelity note: positions, fonts, sizes, palette, and table fills are
 * template-verified. The BMO master uses Arial for Latin titles AND body
 * (its nominal theme "major" font Dax-Medium is wired only to East-Asian /
 * complex-script and is not embedded), so Arial is used throughout.
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#')
// ---------------------------------------------------------------------------

export const BMO_COLORS = {
  bmoBlue: "0079C1", // accent2 — primary brand blue
  darkBlue: "004A7C", // dark2 — secondary blue
  ink: "000000", // dark1 — body text (black)
  white: "FFFFFF",

  gray: "8C8C8C", // accent1 — lines / borders
  grayLight: "C8C8C8", // light2 — header / shading fill
  lightBlue: "CCE4F3", // accent3 — subtle highlight

  yellow: "FFC20E", // accent4
  green: "8DC63F", // accent5
  red: "EC1C24", // accent6
} as const;

/**
 * Drop-in ThemeColors override when BMO style is active. Values from the BMO
 * slide master colorScheme + fontScheme (Latin fonts resolve to Arial).
 */
export const BMO_THEME_COLORS: ThemeColors = {
  dark1: BMO_COLORS.ink, // #000000 — Text1
  dark2: BMO_COLORS.darkBlue, // #004A7C — Text2
  light1: BMO_COLORS.white, // #FFFFFF — Background1
  light2: BMO_COLORS.grayLight, // #C8C8C8 — Background2
  accent1: BMO_COLORS.gray, // #8C8C8C
  accent2: BMO_COLORS.bmoBlue, // #0079C1
  accent3: BMO_COLORS.lightBlue, // #CCE4F3
  accent4: BMO_COLORS.yellow, // #FFC20E
  accent5: BMO_COLORS.green, // #8DC63F
  accent6: BMO_COLORS.red, // #EC1C24
  hyperlink: "0000FF",
  followedHyperlink: "6E298D",
  majorFont: "Arial",
  minorFont: "Arial",
};

/** Ordered series palette — BMO blue first, then contrasting accents. */
export const BMO_CHART_SERIES_PALETTE = [
  BMO_COLORS.bmoBlue,
  BMO_COLORS.darkBlue,
  BMO_COLORS.green,
  BMO_COLORS.yellow,
  BMO_COLORS.red,
  BMO_COLORS.gray,
  BMO_COLORS.lightBlue,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the BMO master)
// ---------------------------------------------------------------------------

export const BMO_SLIDE_DIMENSIONS = { width: 792, height: 612 } as const;

export const BMO_POSITIONS = {
  title: { left: 26.1, top: 43, width: 740.2, height: 25.2 },
  contentStart: { left: 26.1, top: 83 },
  footer: { left: 50.6, top: 554, width: 504, height: 32.6 },
} as const;

export const BMO_LAYOUT_NAMES = [
  "Title Slide",
  "Title and Content",
  "Title Only",
  "Divider Page",
  "Blank",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when BMO style is active
// ---------------------------------------------------------------------------

export const BMO_STYLE_PROMPT = `

## BMO BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. BMO uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts: \`"Arial"\`
NEVER omit fontFamily when BMO style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the BMO master colorScheme)
Use ONLY these BMO brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${BMO_COLORS.ink}
- Text2 / dark2: Dark blue #${BMO_COLORS.darkBlue}
- Background1 / light1: White #${BMO_COLORS.white}
- Background2 / light2: Gray #${BMO_COLORS.grayLight}
- Accent1: Gray #${BMO_COLORS.gray}
- Accent2: BMO blue #${BMO_COLORS.bmoBlue}
- Accent3: Light blue #${BMO_COLORS.lightBlue}
- Accent4: Yellow #${BMO_COLORS.yellow}
- Accent5: Green #${BMO_COLORS.green}
- Accent6: Red #${BMO_COLORS.red}

**Usage:**
- **Primary brand**: BMO blue #${BMO_COLORS.bmoBlue}
- **Text / ink**: Black #${BMO_COLORS.ink}
- **Lines & borders**: Gray #${BMO_COLORS.gray}
- **Background / shading**: Gray #${BMO_COLORS.grayLight}
- **Subtle highlight**: Light blue #${BMO_COLORS.lightBlue}
- **Growth / loss only**: Green #${BMO_COLORS.green} / Red #${BMO_COLORS.red}
- **Chart series palette** (in order): ${BMO_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Always use BMO blue (#${BMO_COLORS.bmoBlue}) as the first/primary series color.

### Typography (authoritative — from the BMO master)
Slide dimensions: 792 × 612 pt.

**Placeholder positions:**
- **Title**: left=26.1, top=43, width=740.2, height=25.2 — "Arial" 20pt, BMO blue (#${BMO_COLORS.bmoBlue}), left-aligned, verticalAlignment="bottom"
- **Content area**: starts at left=26.1, top=83
- **Footer / page number**: left=50.6, top=554, width=504, height=32.6 — "Arial" 7pt, black

**Content text hierarchy (indent levels from the master — all "Arial" 12pt, regular, black #${BMO_COLORS.ink}):**
| Level | Bullet | Indent (left, pt) | Space before |
|-------|--------|-------------------|--------------|
| 1 | • (U+2022 disc) | 18 | 9pt |
| 2 | ▪ (U+25AA small square) | 36 | 3pt |
| 3 | – (U+2013 en-dash) | 54 | 3pt |
| 4 | ◆ (U+25C6 diamond) | 72 | 3pt |
| 5 | › (U+203A) | 90 | 3pt |

- For an emphasized section heading, use bold black or BMO blue (#${BMO_COLORS.bmoBlue}); bullets otherwise inherit the text color (black).
- All text is sentence case, single-spaced, left-justified unless readability requires otherwise.

### Charts (BMO style)
- Font: "Arial", minimum 8pt
- **Primary data color**: always BMO blue (#${BMO_COLORS.bmoBlue}) unless it hurts legibility
- Multi-series: use the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills
- Growth/loss highlight only: green #${BMO_COLORS.green} / red #${BMO_COLORS.red}

### Tables (BMO style)
- Header row: Gray (#${BMO_COLORS.grayLight}) fill with black text, bold
- Body: white fill, black "Arial" text (10pt)
- Borders: thin gray (#${BMO_COLORS.gray}); align numeric columns consistently
- Totals / emphasis rows: bold black, optionally BMO blue (#${BMO_COLORS.bmoBlue}) text

### Shapes & layout (BMO style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes and NOT divider lines
- Rectangular block options: (a) thin Gray (#${BMO_COLORS.gray}) outline, no fill; (b) Gray (#${BMO_COLORS.grayLight}) fill, no outline
- Connectors: solid thin Gray (#${BMO_COLORS.gray}) lines
- Page numbers: 7pt, Gray, "Arial"

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
